/**
 * Route-to-menu access mapping.
 * Used by RouteAccessGuard to block direct URL access when user lacks menu permission.
 * Paths are matched in order; first match wins. More specific paths should come first.
 */

/** Normalize pathname for comparisons (leading slash, trim trailing slashes). */
export function normalizeAccessPath(pathname) {
  if (!pathname || typeof pathname !== 'string') return '';
  let p = pathname.trim();
  if (!p.startsWith('/')) p = `/${p}`;
  while (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p;
}

/** True for /credential-vault/vaultSettings (nested routes blocked same as Sidebar access rules). */
export function isCredentialVaultVaultSettingsPath(normalizedPath) {
  const p = normalizedPath;
  return p === '/credential-vault/vaultSettings' || p.startsWith('/credential-vault/vaultSettings/');
}

/** Add / edit credential form routes — may render without corporate DEK (unlock at save). */
export function isCredentialVaultFormEntryPath(normalizedPath) {
  const p = normalizedPath;
  if (!p || !p.startsWith('/credential-vault')) return false;
  if (p === '/credential-vault/addCredential') return true;
  return /^\/credential-vault\/editCredential\/[^/]+$/.test(p);
}

/** Credential detail — read-only/metadata without corporate unlock (password/copy prompts separately). */
export function isCredentialVaultDetailPath(normalizedPath) {
  const p = normalizedPath;
  if (!p || !p.startsWith('/credential-vault')) return false;
  return /^\/credential-vault\/credentialDetail\/[^/]+$/.test(p);
}

/** Vault settings URL: only when Access Control includes `credential_vault_settings`. */
export function credentialVaultVaultSettingsAllowed(menuKeys) {
  const keys = Array.isArray(menuKeys) ? menuKeys : [];
  return keys.includes('credential_vault_settings');
}

/**
 * Menu keys for the Credentials list module (`/credential-vault/credentialVaultList`, detail, etc.).
 * Includes legacy `credential_vault` until all DBs are migrated.
 */
export const CREDENTIAL_VAULT_LIST_MENU_KEYS = ['credential_vault_list', 'credential_vault'];

export function menuKeysIncludeCredentialVaultList(menuKeys) {
  const keys = Array.isArray(menuKeys) ? menuKeys : [];
  return CREDENTIAL_VAULT_LIST_MENU_KEYS.some((k) => keys.includes(k));
}

/** Credentials list / entries (not Credentials Vault settings UI). */
export function isCredentialVaultCredentialModulePath(normalizedPath) {
  if (!normalizedPath || !normalizedPath.startsWith('/credential-vault')) return false;
  return !isCredentialVaultVaultSettingsPath(normalizedPath);
}

/**
 * When `routePaths` from my-access is non-empty, it reflects which section rows have a route_path in DB.
 * If list keys appear in menuKeys but the list route is missing (data inconsistency), deny the module.
 */
export function credentialVaultCredentialModuleAllowed(menuKeys, routePaths) {
  if (!menuKeysIncludeCredentialVaultList(menuKeys)) return false;
  const paths = Array.isArray(routePaths) ? routePaths : [];
  if (paths.length === 0) return true;
  const corporateList = normalizeAccessPath('/credential-vault/credentialVaultList');
  const personalList = normalizeAccessPath('/credential-vault/personalVaultList');
  return paths.some((p) => {
    const n = normalizeAccessPath(p);
    return n === corporateList || n === personalList;
  });
}

/**
 * Sidebar sub-item visibility: requires menu_key in allow-list, and when my-access returned a non-empty `routePaths`
 * array, the item's primary `to` path must appear there (fixes stale list keys without list route rows).
 */
export function submenuAllowedByMyAccess(menuKeys, routePaths, subItem) {
  const keys = Array.isArray(menuKeys) ? menuKeys : [];
  const k = subItem?.menu_key;
  if (k === 'credential_vault_list') {
    if (!menuKeysIncludeCredentialVaultList(keys)) return false;
  } else if (k === 'credential_vault_settings') {
    if (!keys.includes('credential_vault_settings') && !menuKeysIncludeCredentialVaultList(keys)) {
      return false;
    }
  } else if (k && !keys.includes(k)) return false;
  const paths = Array.isArray(routePaths) ? routePaths : [];
  if (paths.length === 0 || !subItem?.to) return true;
  const t = normalizeAccessPath(subItem.to);
  if (!t || t === '#') return true;
  if (paths.some((p) => normalizeAccessPath(p) === t)) return true;
  const corporateList = normalizeAccessPath('/credential-vault/credentialVaultList');
  const personalList = normalizeAccessPath('/credential-vault/personalVaultList');
  const vaultSettingsPath = normalizeAccessPath('/credential-vault/vaultSettings');
  if (t === vaultSettingsPath) {
    return paths.some((p) => normalizeAccessPath(p) === vaultSettingsPath) ||
      paths.some((p) => normalizeAccessPath(p) === corporateList);
  }
  if (t === personalList) {
    return paths.some((p) => normalizeAccessPath(p) === corporateList);
  }
  return false;
}

export const ROUTE_ACCESS_CONFIG = [
  // Task module - task_list
  { path: '/task/tasklist', menuKeys: ['task_list'] },
  { path: '/task/addtask', menuKeys: ['task_list'] },
  { path: '/task/edittask', menuKeys: ['task_list'] },
  { path: '/task/taskboard', menuKeys: ['task_list'] },
  { path: '/task/taskdetails', menuKeys: ['task_list'] },
  // Task - worksheet
  { path: '/task/worksheet', menuKeys: ['worksheet'] },
  // Task - project
  { path: '/task/projectlist', menuKeys: ['project'] },
  { path: '/task/addproject', menuKeys: ['project'] },
  { path: '/task/editproject', menuKeys: ['project'] },
  { path: '/task/projectdetails', menuKeys: ['project'] },
  // Settings - users
  { path: '/settings/userlist', menuKeys: ['settings_users'] },
  { path: '/settings/adduser', menuKeys: ['settings_users'] },
  { path: '/settings/edituser', menuKeys: ['settings_users'] },
  // Settings - user settings
  { path: '/settings/userSettings', menuKeys: ['settings_user_settings'] },
  // Settings - access control
  { path: '/settings/accessControl', menuKeys: ['settings_access_control'] },
  // Settings - branches
  { path: '/settings/branchList', menuKeys: ['settings_branches'] },
  { path: '/settings/addBranch', menuKeys: ['settings_branches'] },
  { path: '/settings/editBranch', menuKeys: ['settings_branches'] },
  // Settings - departments
  { path: '/settings/departmentList', menuKeys: ['settings_departments'] },
  { path: '/settings/addDepartment', menuKeys: ['settings_departments'] },
  { path: '/settings/editDepartment', menuKeys: ['settings_departments'] },
  // Settings - sub departments
  { path: '/settings/subDepartmentList', menuKeys: ['settings_sub_departments'] },
  { path: '/settings/addSubDepartment', menuKeys: ['settings_sub_departments'] },
  { path: '/settings/editSubDepartment', menuKeys: ['settings_sub_departments'] },
  // Settings - designations
  { path: '/settings/designationList', menuKeys: ['settings_designations'] },
  { path: '/settings/addDesignation', menuKeys: ['settings_designations'] },
  // Staff
  { path: '/staff/', menuKeys: ['settings_staff'] },
  // Vehicle
  { path: '/vehicle/', menuKeys: ['settings_vehicle'] },
  // Credentials Vault (Settings menu) — same gate as credential lists unless credential_vault_settings is granted alone
  {
    path: '/credential-vault/vaultSettings',
    menuKeys: ['credential_vault_settings', ...CREDENTIAL_VAULT_LIST_MENU_KEYS],
  },
  // Credentials Vault — routes under /credential-vault except vaultSettings (vaultSettings uses entry above)
  { path: '/credential-vault', menuKeys: [...CREDENTIAL_VAULT_LIST_MENU_KEYS] },
  // Documents
  { path: '/documents/dashboard', menuKeys: ['doc_dashboard'] },
  { path: '/documents/mydocuments', menuKeys: ['doc_my_documents'] },
  { path: '/documents/folder/', menuKeys: ['doc_my_documents'] },
  { path: '/documents/details/', menuKeys: ['doc_my_documents', 'doc_shared', 'doc_favorites'] },
  { path: '/documents/edit/', menuKeys: ['doc_my_documents'] },
  { path: '/documents/shared', menuKeys: ['doc_shared'] },
  { path: '/documents/favorites', menuKeys: ['doc_favorites'] },
  { path: '/documents/upload', menuKeys: ['doc_my_documents'] },
  { path: '/documents/documentClassifications', menuKeys: ['doc_classifications'] },
  { path: '/documents/addDocumentClassification', menuKeys: ['doc_classifications'] },
  { path: '/documents/editDocumentClassification', menuKeys: ['doc_classifications'] },
];

/**
 * Get required menu keys for a path.
 * @param {string} pathname - e.g. /task/tasklist, /task/addtask
 * @returns {string[]|null} - Required menu keys (any one grants access), or null if no restriction
 */
export function getRequiredMenuKeys(pathname) {
  if (!pathname || typeof pathname !== 'string') return null;
  const normalized = normalizeAccessPath(pathname);
  if (!normalized) return null;

  for (const { path, menuKeys } of ROUTE_ACCESS_CONFIG) {
    if (normalized.startsWith(path) || normalized === path) {
      if (path === '/credential-vault' && isCredentialVaultVaultSettingsPath(normalized)) {
        continue;
      }
      return menuKeys;
    }
  }
  return null;
}
